import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";

type Summary = {
  authorized: boolean;
  id?: string;
  business_date: string;
  party_count: number;
  gross_sales: number;
  card_sales: number;
  cash_sales: number;
  cash_opening: number;
  cash_counted?: number | null;
  cash_expenses: number;
  status: "draft" | "closed";
  square_synced_at?: string | null;
  notes?: string | null;
};
type Expense = {
  id: string;
  entry_type: string;
  category: string;
  description: string;
  amount_yen: number;
  receipt_path: string;
};
const yen = new Intl.NumberFormat("ja-JP", {
  style: "currency",
  currency: "JPY",
  maximumFractionDigits: 0,
});
const today = () =>
  new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Tokyo" }).format(
    new Date(),
  );

export default function GoldysClosingPage() {
  const [date, setDate] = useState(today()),
    [summary, setSummary] = useState<Summary | null>(null),
    [expenses, setExpenses] = useState<Expense[]>([]),
    [loading, setLoading] = useState(true),
    [saving, setSaving] = useState(false),
    [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState({
    parties: "0",
    opening: "20000",
    counted: "20000",
    notes: "",
  });
  const [expense, setExpense] = useState({
    type: "operating_expense",
    category: "",
    description: "",
    amount: "",
  });
  const [receipt, setReceipt] = useState<File | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("get_goldys_daily_summary", {
      p_date: date,
    });
    if (error) {
      setMessage("日次締めを読み込めませんでした。");
      setLoading(false);
      return;
    }
    const s = data as Summary;
    setSummary(s);
    setForm({
      parties: String(s.party_count || 0),
      opening: "20000",
      counted: String(s.cash_counted ?? 20000),
      notes: s.notes || "",
    });
    if (s.id) {
      const r = await supabase
        .from("goldys_cash_expenses")
        .select("id,entry_type,category,description,amount_yen,receipt_path")
        .eq("closing_id", s.id)
        .order("created_at");
      setExpenses((r.data || []) as Expense[]);
    } else setExpenses([]);
    setLoading(false);
  }, [date]);
  useEffect(() => {
    void load();
  }, [load]);
  const expected = useMemo(
    () =>
      Number(form.opening || 0) +
      (summary?.cash_sales || 0) -
      expenses.reduce((a, e) => a + e.amount_yen, 0),
    [form.opening, summary, expenses],
  );
  const save = async () => {
    setSaving(true);
    setMessage(null);
    const { error } = await supabase.rpc("save_goldys_daily_closing", {
      p_date: date,
      p_party_count: Number(form.parties),
      p_cash_opening: Number(form.opening),
      p_cash_counted: Number(form.counted),
      p_notes: form.notes || null,
    });
    setSaving(false);
    if (error) setMessage("保存できませんでした。");
    else {
      setMessage("途中保存しました。");
      await load();
    }
  };
  const addExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!receipt) {
      setMessage("レシート写真を選んでください。");
      return;
    }
    setSaving(true);
    let closingId = summary?.id;
    if (!closingId) {
      const r = await supabase.rpc("save_goldys_daily_closing", {
        p_date: date,
        p_party_count: Number(form.parties),
        p_cash_opening: Number(form.opening),
        p_cash_counted: Number(form.counted),
        p_notes: form.notes || null,
      });
      if (r.error) {
        setSaving(false);
        setMessage("先に日次データを保存できませんでした。");
        return;
      }
      closingId = r.data as string;
    }
    const ext = (receipt.name.split(".").pop() || "jpg").toLowerCase();
    const path = `${date}/${closingId}/${crypto.randomUUID()}.${ext}`;
    const uploaded = await supabase.storage
      .from("goldys-receipts")
      .upload(path, receipt, { contentType: receipt.type, upsert: false });
    if (uploaded.error) {
      setSaving(false);
      setMessage("レシートをアップロードできませんでした。");
      return;
    }
    const r = await supabase.rpc("add_goldys_cash_expense", {
      p_closing_id: closingId,
      p_type: expense.type,
      p_category: expense.category,
      p_description: expense.description,
      p_amount: Number(expense.amount),
      p_receipt_path: path,
    });
    setSaving(false);
    if (r.error) setMessage("現金出金を保存できませんでした。");
    else {
      setExpense({
        type: "operating_expense",
        category: "",
        description: "",
        amount: "",
      });
      setReceipt(null);
      await load();
    }
  };
  const close = async () => {
    setSaving(true);
    setMessage("Squareの最終売上を同期しています…");
    const syncResult = await supabase.functions.invoke("sync-goldys-square", {
      body: { date },
    });
    if (syncResult.error || syncResult.data?.success !== true) {
      setSaving(false);
      setMessage("Squareの最終同期に失敗したため、締め処理を中止しました。");
      return;
    }
    await load();
    await save();
    const s = (await supabase.rpc("get_goldys_daily_summary", { p_date: date }))
      .data as Summary;
    if (!s?.id) return;
    setSaving(true);
    const { error } = await supabase.rpc("close_goldys_day", {
      p_closing_id: s.id,
    });
    setSaving(false);
    if (error) setMessage("締め処理を完了できませんでした。");
    else {
      setMessage("日次締めを完了し、損益へ反映しました。");
      await load();
    }
  };
  return (
    <div className="p-4 md:p-7 max-w-6xl mx-auto space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold tracking-[.16em] uppercase text-[#9A6A2F]">
            Goldy’s
          </p>
          <h1 className="text-2xl font-extrabold mt-1">日次締め・現金管理</h1>
          <p className="text-sm text-foreground-600 mt-1">
            Square売上、組数、現金出金とレジ残高を一つにまとめます。
          </p>
        </div>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border rounded-lg px-3 py-2 bg-white"
        />
      </header>
      {message && (
        <div className="rounded-lg bg-primary-50 text-primary-800 px-4 py-3 text-sm">
          {message}
        </div>
      )}
      {loading ? (
        <div className="p-10 text-center">読み込み中…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ["組数", `${summary?.party_count || 0}組`],
              ["Square売上", yen.format(summary?.gross_sales || 0)],
              [
                "現金出金",
                yen.format(expenses.reduce((a, e) => a + e.amount_yen, 0)),
              ],
              [
                "締め状況",
                summary?.status === "closed" ? "締め済み" : "未締め",
              ],
            ].map(([l, v]) => (
              <div key={l} className="rounded-xl border bg-white p-4">
                <p className="text-xs text-foreground-500">{l}</p>
                <p className="text-xl font-extrabold mt-2">{v}</p>
              </div>
            ))}
          </div>
          <section className="rounded-xl border bg-white p-5 space-y-4">
            <div className="flex justify-between">
              <div>
                <h2 className="font-bold">売上・レジ確認</h2>
                <p className="text-xs text-foreground-500 mt-1">
                  Square同期{" "}
                  {summary?.square_synced_at
                    ? new Date(summary.square_synced_at).toLocaleString("ja-JP")
                    : "未同期"}
                </p>
              </div>
              <span className="text-xs rounded-full bg-background-100 px-3 py-1 h-fit">
                カード {yen.format(summary?.card_sales || 0)} / 現金{" "}
                {yen.format(summary?.cash_sales || 0)}
              </span>
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <Label text="来店組数">
                <input
                  type="number"
                  min="0"
                  value={form.parties}
                  onChange={(e) =>
                    setForm({ ...form, parties: e.target.value })
                  }
                />
              </Label>
              <div className="rounded-lg bg-background-100 p-3">
                <p className="text-xs text-foreground-500">
                  釣銭準備金（固定）
                </p>
                <p className="font-bold mt-2">¥20,000</p>
              </div>
              <Label text="封筒へ抜く前に数えた現金">
                <input
                  type="number"
                  min="0"
                  value={form.counted}
                  onChange={(e) =>
                    setForm({ ...form, counted: e.target.value })
                  }
                />
              </Label>
              <div className="rounded-lg bg-background-100 p-3">
                <p className="text-xs text-foreground-500">計算上の現金</p>
                <p className="font-bold mt-2">{yen.format(expected)}</p>
                <p
                  className={`text-xs mt-1 ${Number(form.counted) - expected === 0 ? "text-primary-700" : "text-red-700"}`}
                >
                  過不足 {yen.format(Number(form.counted) - expected)}
                </p>
              </div>
            </div>
            {Number(form.counted) === expected && (
              <div className="rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-800">
                <b>一致しています。</b>{" "}
                {yen.format(Math.max(0, expected - 20000))}{" "}
                を封筒へ入れ、レジに2万円を残してください。
              </div>
            )}
            <Label text="メモ">
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Label>
            <button
              disabled={saving}
              onClick={save}
              className="px-4 py-2 rounded-lg border font-bold"
            >
              途中保存
            </button>
          </section>
          <section className="rounded-xl border bg-white p-5">
            <h2 className="font-bold">現金で支払った経費</h2>
            <form
              onSubmit={addExpense}
              className="grid md:grid-cols-5 gap-3 mt-4 items-end"
            >
              <Label text="区分">
                <select
                  value={expense.type}
                  onChange={(e) =>
                    setExpense({ ...expense, type: e.target.value })
                  }
                >
                  <option value="operating_expense">経費</option>
                  <option value="cost_of_sales">仕入</option>
                </select>
              </Label>
              <Label text="分類">
                <input
                  required
                  placeholder="食材・消耗品など"
                  value={expense.category}
                  onChange={(e) =>
                    setExpense({ ...expense, category: e.target.value })
                  }
                />
              </Label>
              <Label text="内容">
                <input
                  required
                  value={expense.description}
                  onChange={(e) =>
                    setExpense({ ...expense, description: e.target.value })
                  }
                />
              </Label>
              <Label text="金額">
                <input
                  required
                  type="number"
                  min="1"
                  value={expense.amount}
                  onChange={(e) =>
                    setExpense({ ...expense, amount: e.target.value })
                  }
                />
              </Label>
              <Label text="レシート">
                <input
                  required
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setReceipt(e.target.files?.[0] || null)}
                />
              </Label>
              <button
                disabled={saving}
                className="md:col-span-5 py-2 rounded-lg bg-[#9A6A2F] text-white font-bold"
              >
                レシート付き出金を追加
              </button>
            </form>
            <div className="mt-4 divide-y">
              {expenses.map((e) => (
                <div key={e.id} className="py-3 flex justify-between gap-3">
                  <div>
                    <p className="font-semibold">
                      {e.category} · {e.description}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {e.entry_type === "cost_of_sales" ? "仕入" : "経費"} ·
                      レシート保存済み
                    </p>
                  </div>
                  <strong>{yen.format(e.amount_yen)}</strong>
                </div>
              ))}
            </div>
          </section>
          <button
            disabled={
              saving ||
              summary?.status === "closed" ||
              Number(form.counted) !== expected
            }
            onClick={close}
            className="w-full py-3 rounded-xl bg-primary-800 text-white font-extrabold disabled:opacity-50"
          >
            {summary?.status === "closed"
              ? "締め処理済み"
              : Number(form.counted) !== expected
                ? "現金差額を解消してください"
                : "一致確認後、封筒へ移して締めを確定"}
          </button>
        </>
      )}
    </div>
  );
}
function Label({
  text,
  children,
}: {
  text: string;
  children: React.ReactNode;
}) {
  return (
    <label className="text-sm block">
      {text}
      <div className="mt-1 [&>input]:w-full [&>input]:border [&>input]:rounded-lg [&>input]:px-3 [&>input]:py-2 [&>select]:w-full [&>select]:border [&>select]:rounded-lg [&>select]:px-3 [&>select]:py-2 [&>textarea]:w-full [&>textarea]:border [&>textarea]:rounded-lg [&>textarea]:px-3 [&>textarea]:py-2">
        {children}
      </div>
    </label>
  );
}
